import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Sparkles, FolderOpen, Save, FileDown, Sun, Moon, Monitor, Folder, FileText,
  ChevronRight, ChevronDown, Search, Settings, Shield, LogOut, Lock,
  Edit3, Check, Crown, Loader2, ExternalLink, AlertCircle, HelpCircle, Info,
  MoreHorizontal,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useToleranceStore } from '../../stores/toleranceStore';
import { useProjectStore } from '../../stores/projectStore';
import { useAuthStore, selectIsPro } from '../../stores/authStore';
import { useTheme } from '../../hooks/useTheme';
import { trackEvent } from '../../lib/analytics';
import ConfirmDialog from '../ConfirmDialog';
import Brand from '../Brand';
import SaveTcpDialog from '../SaveTcpDialog';
import { WissenMenuLink } from './WissenHinweis';
import { saveStatusPill } from './saveStatus';
import VersionMenuItems from '../versions/VersionMenuItems';
import { toTcpString, parseTcp, TcpParseError } from '../../utils/tcpFormat';
import { toast } from '../../lib/toast';

// Heavy modals — same lazy split as the old Header. jsPDF + the template
// registry only ship if the user clicks Export / Vorlagen.
const ExportDialog = lazy(() => import('../export/ExportDialog'));
const TemplateGalleryModal = lazy(() => import('../TemplateGalleryModal'));
const NewProjectModal = lazy(() => import('../NewProjectModal'));

// ─── Theme switch (sun / moon segmented) ───────────────────────────
// Lives in the header next to the user menu. Reads + writes via the
// useTheme hook, which handles localStorage + body class + FOUC.
function ThemeSwitch() {
  const [theme, setTheme] = useTheme();
  const opts = [
    { id: 'light',  icon: Sun,     title: 'Hell' },
    { id: 'system', icon: Monitor, title: 'Systemstandard folgen' },
    { id: 'dark',   icon: Moon,    title: 'Dunkel' },
  ];
  return (
    <div
      role="group"
      aria-label="Theme"
      className="inline-flex bg-surface-2 border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: 2, height: 34 }}
    >
      {opts.map((o) => {
        const Icon = o.icon;
        const active = theme === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => setTheme(o.id)}
            aria-pressed={active}
            title={o.title}
            className={`inline-flex items-center justify-center transition-colors ${
              active ? 'bg-surface text-brand shadow-sm' : 'text-ink-3 hover:text-ink-2'
            }`}
            style={{ width: 28, height: 28, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            <Icon size={13} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// ─── Breadcrumb (Projekt / Berechnung) ─────────────────────────────
// Absorbs the cloud-save state machine + project/calculation pickers
// + inline name editor that used to live in the standalone ProjectBar
// (deleted in this commit). Two chip-shaped dropdowns side by side,
// each opening a card menu underneath. Free users see Pro upsells in
// the project menu and just an inline name editor in the calc chip.

// Small upsell card shown inside Pro-gated dropdowns for Free users.
function ProMenuUpsell({ title, body }) {
  return (
    <div className="p-5 text-center space-y-3">
      <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
        <Crown size={20} className="text-amber-500" />
      </div>
      <h4 className="text-sm font-black text-ink">{title}</h4>
      <p className="text-[11px] text-ink-3 leading-relaxed">{body}</p>
      <Link
        to="/settings/billing"
        className="block w-full py-2.5 bg-amber-700 hover:bg-amber-600 text-white rounded-xl text-xs font-black transition-all"
      >
        Pro-Plan ansehen
      </Link>
    </div>
  );
}

function ProjectChip({ onOpenNewProject }) {
  const navigate = useNavigate();
  const isPro = useAuthStore(selectIsPro);
  const projects = useProjectStore((s) => s.projects);
  const currentProject = useProjectStore((s) => s.currentProject);
  const selectProject = useProjectStore((s) => s.selectProject);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const label = currentProject?.name || (isPro ? 'Kein Projekt' : 'Nicht zugeordnet');
  const empty = !currentProject;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={empty ? 'Projekt wählen' : `Projekt: ${label}`}
        className="inline-flex items-center gap-1.5 text-xs hover:bg-surface transition-colors"
        style={{ height: 28, padding: '0 10px', border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer' }}
      >
        <Folder size={13} className={empty ? 'text-ink-4' : 'text-ink-3'} aria-hidden="true" />
        <span className={`font-medium truncate max-w-[18ch] ${empty ? 'text-ink-4' : 'text-ink-3'}`}>
          {label}
        </span>
        <ChevronDown size={11} className={`text-ink-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full bg-surface border border-line-strong shadow-2xl overflow-hidden z-50"
          style={{ marginTop: 6, width: 280, borderRadius: 'var(--radius-md)' }}
        >
          {isPro ? (
            <>
              <div className="p-2 border-b border-line">
                <button
                  type="button"
                  onClick={() => { setOpen(false); onOpenNewProject(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-soft rounded-md transition-colors"
                >
                  <Plus size={14} aria-hidden="true" />
                  Neues Projekt anlegen …
                </button>
              </div>
              <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: 6 }}>
                {projects.length === 0 ? (
                  <p className="px-3 py-4 text-[11px] text-ink-4 italic text-center">Noch keine Projekte</p>
                ) : (
                  projects.map((p) => {
                    const active = currentProject?.id === p.id;
                    return (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => { selectProject(p); setOpen(false); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectProject(p); setOpen(false); } }}
                        className={`group/row flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer ${
                          active ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-surface-2'
                        }`}
                      >
                        <div className="w-6 h-6 rounded bg-surface-2 border border-line flex items-center justify-center shrink-0 overflow-hidden">
                          {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-contain" /> : <Folder size={12} aria-hidden="true" />}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-xs font-semibold truncate">{p.name}</span>
                          {p.company_name && (
                            <span className="text-[10px] text-ink-4 truncate">{p.company_name}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setOpen(false); navigate('/projects', { state: { drilldownProjectId: p.id } }); }}
                          className="p-1 text-ink-4 hover:text-brand rounded opacity-0 group-hover/row:opacity-100"
                          title={`Berechnungen von „${p.name}" anzeigen`}
                        >
                          <ExternalLink size={12} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="p-2 border-t border-line">
                <Link
                  to="/projects"
                  onClick={() => setOpen(false)}
                  className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-ink-3 hover:text-ink-2 transition-colors"
                >
                  Alle Projekte verwalten →
                </Link>
              </div>
            </>
          ) : (
            <ProMenuUpsell
              title="Projekte sind ein Pro-Feature"
              body="Im Free-Plan exportierst du Toleranzberechnungen als CSV oder PDF. Mit Pro legst du Projekte an und speicherst beliebig viele Berechnungen in der Cloud."
            />
          )}
        </div>
      )}
    </div>
  );
}

function CalcChip() {
  const isPro = useAuthStore(selectIsPro);
  const {
    calculations, currentProject, currentCalculation,
    createCalculation, loadCalculation, saveCurrentCalculation, saving, saveError,
  } = useProjectStore(useShallow((s) => ({
    calculations: s.calculations,
    currentProject: s.currentProject,
    currentCalculation: s.currentCalculation,
    createCalculation: s.createCalculation,
    loadCalculation: s.loadCalculation,
    saveCurrentCalculation: s.saveCurrentCalculation,
    saving: s.saving,
    saveError: s.saveError,
  })));
  const {
    calculationName, setCalculationName, calculationIndex, isDirty, elementsCount, preview,
  } = useToleranceStore(useShallow((s) => ({
    calculationName: s.calculationName,
    setCalculationName: s.setCalculationName,
    calculationIndex: s.calculationIndex,
    isDirty: s.isDirty,
    elementsCount: s.elements.length,
    preview: s.preview,
  })));

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(calculationName);
  const ref = useRef(null);

  // Sync the draft when the underlying name changes from outside (e.g.
  // loading another calc). Render-time prop watch — same pattern as
  // NumInput, no useEffect setState lint hit.
  const [lastName, setLastName] = useState(calculationName);
  if (calculationName !== lastName) {
    setLastName(calculationName);
    setNameDraft(calculationName);
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const trTag = calculationIndex
    ? `TR-${String(calculationIndex).padStart(3, '0')}`
    : 'TR-NEW';

  const commitName = () => {
    setCalculationName(nameDraft);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!isPro || !currentProject) return;
    try {
      const isFirstSave = !currentCalculation;
      await saveCurrentCalculation();
      trackEvent(isFirstSave ? 'Calculation First Save' : 'Calculation Saved');
    } catch (err) {
      toast.error(err.message || 'Speichern fehlgeschlagen');
    }
  };

  const handleNewCalc = async () => {
    if (!isPro || !currentProject) return;
    setOpen(false);
    try {
      await createCalculation();
      trackEvent('Calculation Created');
    } catch (err) {
      toast.error(err.message || 'Konnte keine neue Berechnung anlegen');
    }
  };

  const handlePickCalc = async (calc) => {
    setOpen(false);
    if (calc.id === currentCalculation?.id) return;
    try {
      await loadCalculation(calc);
    } catch (err) {
      toast.error(err.message || 'Laden fehlgeschlagen');
    }
  };

  // Save-state pill that sits on the chip itself.
  const pill = saveStatusPill({ isPro, currentProject, currentCalculation, saving, saveError, isDirty, preview });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Berechnung: ${calculationName}`}
        className="inline-flex items-center gap-1.5 text-xs text-ink-2 font-semibold hover:bg-surface transition-colors"
        style={{ height: 28, padding: '0 10px', border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer' }}
      >
        <FileText size={13} className="text-ink-3" aria-hidden="true" />
        {isPro && (
          <span className="num text-ink-4" style={{ fontSize: 10, fontWeight: 700 }}>{trTag}</span>
        )}
        <span className="truncate max-w-[18ch]">{calculationName}</span>
        {pill && (
          <span title={pill.title} className={`text-[11px] leading-none ${pill.cls}`}>{pill.label}</span>
        )}
        <ChevronDown size={11} className={`text-ink-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full bg-surface border border-line-strong shadow-2xl overflow-hidden z-50"
          style={{ marginTop: 6, width: 320, borderRadius: 'var(--radius-md)' }}
        >
          {/* Inline name editor (always available — Free uses it for
              PDF / CSV / .tcp metadata even without cloud-save). */}
          <div className="p-3 border-b border-line">
            <div className="text-[10px] font-black uppercase tracking-widest text-ink-4 mb-1.5">Bezeichnung</div>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitName(); } if (e.key === 'Escape') { setNameDraft(calculationName); setEditing(false); } }}
                  placeholder="Bezeichnung der Berechnung"
                  className="flex-1 bg-surface-2 border border-line text-ink rounded-md px-2 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-brand-soft"
                />
                <button
                  type="button"
                  onClick={commitName}
                  className="p-1 text-ok hover:bg-surface-2 rounded"
                  title="Übernehmen"
                >
                  <Check size={14} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={!!preview}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded-md hover:bg-surface-2 text-xs text-ink font-semibold disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <span className="flex-1 truncate">{calculationName || 'Bezeichnung der Berechnung'}</span>
                <Edit3 size={12} className="text-ink-4" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Pro-only: TR-XXX list + Neue Berechnung */}
          {isPro && currentProject && (
            <>
              <div className="p-2 border-b border-line">
                <button
                  type="button"
                  onClick={handleNewCalc}
                  disabled={saving}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-soft rounded-md transition-colors disabled:opacity-50"
                >
                  <Plus size={14} aria-hidden="true" />
                  Neue Berechnung
                </button>
              </div>
              <div style={{ maxHeight: '40vh', overflowY: 'auto', padding: 6 }}>
                {calculations.length === 0 ? (
                  <p className="px-3 py-4 text-[11px] text-ink-4 italic text-center">Noch keine Berechnungen</p>
                ) : (
                  calculations.map((c) => {
                    const active = c.id === currentCalculation?.id;
                    const tag = `TR-${String(c.calculation_index).padStart(3, '0')}`;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handlePickCalc(c)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left ${
                          active ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-surface-2'
                        }`}
                      >
                        <div className="w-8 h-8 rounded bg-surface-2 border border-line flex items-center justify-center overflow-hidden shrink-0">
                          {c.sketch_url
                            ? <img src={c.sketch_url} alt="" className="w-full h-full object-contain bg-surface" />
                            : <FileText size={12} className="text-ink-4" aria-hidden="true" />}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="num text-[10px] text-ink-4 font-semibold">{tag}</span>
                          <span className="text-xs font-semibold truncate">{c.name}</span>
                        </div>
                        {active && <Check size={13} className="text-brand shrink-0" aria-hidden="true" />}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Cloud-save action (Pro). The .tcp save in the header action
              group is a SEPARATE flow (local file download); this one
              persists to Supabase. */}
          {isPro && (
            <div className="p-2 border-t border-line">
              {currentProject ? (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!!preview || saving || elementsCount === 0 || (!isDirty && !!currentCalculation)}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold rounded-md transition-colors ${
                    saving ? 'bg-surface-2 text-ink-3 cursor-not-allowed' :
                    isDirty ? 'bg-brand text-white hover:bg-[var(--color-brand-2)]' :
                    currentCalculation ? 'bg-surface-2 text-ok' :
                    'bg-surface-2 text-ink-3 cursor-not-allowed'
                  }`}
                >
                  {saving
                    ? <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                    : isDirty ? <Save size={13} aria-hidden="true" />
                    : currentCalculation ? <Check size={13} aria-hidden="true" />
                    : <Save size={13} aria-hidden="true" />}
                  {saving ? 'Speichert …'
                    : isDirty ? 'In der Cloud speichern'
                    : currentCalculation ? 'Gespeichert'
                    : 'Keine Änderungen'}
                </button>
              ) : null}
              {currentProject && saveError && (
                <p role="alert" className="mt-1.5 px-1 text-[11px] leading-snug text-err">
                  Nicht gespeichert: {saveError}
                </p>
              )}
              {currentProject ? null : (
                <div className="px-3 py-2 text-[11px] text-ink-4 text-center flex items-center justify-center gap-2">
                  <AlertCircle size={12} aria-hidden="true" />
                  Erst Projekt wählen
                </div>
              )}
            </div>
          )}

          {isPro && currentProject && currentCalculation && (
            <VersionMenuItems onDone={() => setOpen(false)} disabled={!!preview} />
          )}

          {!isPro && (
            <div className="border-t border-line">
              <ProMenuUpsell
                title="Cloud-Speichern im Pro-Plan"
                body="Cloud-Berechnungen + .tcp-Dateien + unbegrenzte Projekte."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Breadcrumb({ onOpenNewProject }) {
  return (
    <div
      className="inline-flex items-center bg-surface-2 border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: 2, marginLeft: 8 }}
    >
      <ProjectChip onOpenNewProject={onOpenNewProject} />
      <ChevronRight size={11} className="text-ink-4 mx-0.5" aria-hidden="true" />
      <CalcChip />
    </div>
  );
}

// ─── Search input (⌘K command-palette trigger) ────────────────────
// Click + keyboard shortcut share the same surface: this button
// dispatches `toleranz:open-cmdk` which App.jsx's global listener
// reacts to (so does Cmd/Ctrl-K from anywhere).
function SearchInput() {
  // Volle Suchleiste ab xl (≥ 1280 px) — darunter wird's im Header zu
  // eng (Brand + Breadcrumb + 5 Action-Buttons + UserMenu konkurrieren
  // um den Platz). Schmaler Icon-Knopf bleibt ab md sichtbar, damit
  // Tablet- und Snap-Layout-Setups den ⌘K-Trigger weiter klickbar
  // haben; Shortcut funktioniert ohnehin global.
  return (
    <>
      <button
        type="button"
        title="Befehlspalette öffnen (⌘K / Strg+K)"
        onClick={() => window.dispatchEvent(new Event('toleranz:open-cmdk'))}
        className="hidden 2xl:inline-flex items-center gap-2 px-2.5 text-xs text-ink-3 bg-surface-2 border border-line hover:bg-surface hover:text-ink-2 transition-colors"
        style={{ height: 32, borderRadius: 'var(--radius-md)', width: 240, cursor: 'pointer' }}
      >
        <Search size={13} aria-hidden="true" />
        <span className="truncate flex-1 text-left">Projekte, Aktionen suchen…</span>
        <kbd className="text-[10px] font-semibold bg-surface border border-line px-1 py-0.5" style={{ borderRadius: 4 }}>
          ⌘K
        </kbd>
      </button>
      <button
        type="button"
        title="Befehlspalette öffnen (⌘K / Strg+K)"
        onClick={() => window.dispatchEvent(new Event('toleranz:open-cmdk'))}
        className="hidden md:inline-flex 2xl:hidden items-center justify-center text-ink-3 bg-surface-2 border border-line hover:bg-surface hover:text-ink-2 transition-colors"
        style={{ height: 32, width: 32, borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
      >
        <Search size={14} aria-hidden="true" />
      </button>
    </>
  );
}

// ─── Action button ─────────────────────────────────────────────────

function HeaderButton({ icon: Icon, label, onClick, primary, kbd, disabled, lock, title, tour }) {
  // Label + kbd verstecken sich unter xl (1280 px), damit die Action-
  // Reihe im Header bei halbierten 2K-Monitoren (≈ 1255 px) nicht
  // überläuft. Icon + Title-Tooltip bleiben — die Buttons sind
  // weiter erreichbar, nur kompakter. Primary-Button (Export) behält
  // sein Label, weil er der CTA der Reihe ist und farblich abgesetzt.
  const labelHiddenClass = primary ? '' : 'hidden 2xl:inline';
  return (
    <button
      data-tour={tour}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      aria-label={title || label}
      className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors active:translate-y-px relative ${
        primary
          ? 'bg-brand text-white border-brand shadow-md hover:bg-[var(--color-brand-2)] disabled:opacity-50'
          : 'bg-surface text-ink-2 border-line-strong hover:bg-surface-2 disabled:opacity-50'
      }`}
      style={{
        height: 34,
        // Padding etwas knapper bei Icon-only-State, damit die Buttons
        // nicht wie unausgefüllte Kästen aussehen. Primary behält sein
        // großzügiges Padding, da er immer das Label trägt.
        padding: primary ? '0 14px' : '0 10px',
        border: '1px solid', borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {Icon && <Icon size={14} aria-hidden="true" />}
      <span className={labelHiddenClass}>{label}</span>
      {kbd && (
        <kbd
          className="hidden 2xl:inline-block text-[10px] font-semibold bg-surface-2 border border-line ml-1 px-1 py-0.5"
          style={{ borderRadius: 4 }}
        >
          {kbd}
        </kbd>
      )}
      {lock && <Lock size={10} className="text-ink-4 ml-0.5" aria-hidden="true" />}
    </button>
  );
}

// ─── Actions overflow menu (≤ 1075 px) ────────────────────────────
// Bei sehr engen Headers (z.B. Drittel-Snap auf 4K oder Tablet
// landscape) reichen Icon-only-Buttons nicht mehr — die Reihe
// Vorlagen/Laden/Speichern/Export wandert in ein Drei-Punkte-Menü.
// „Neu" bleibt außerhalb, weil es die häufigste Aktion ist.
// Mini-Trenner zwischen Item-Gruppen im Overflow-Menu — gerendert
// als sentinel-Eintrag mit `divider: true`, damit die items-Liste
// linear bleibt und das responsive Hide-Verhalten konsistent funktioniert.
function ActionsOverflowMenu({ items }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-tour="more-menu"
        title="Weitere Aktionen"
        className="inline-flex items-center justify-center bg-surface text-ink-2 border border-line-strong hover:bg-surface-2 transition-colors"
        style={{ height: 34, width: 34, borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 bg-surface border border-line-strong shadow-2xl overflow-hidden z-50"
          style={{ width: 220, borderRadius: 'var(--radius-md)' }}
        >
          <div className="py-1">
            {items.map((item, idx) => {
              if (item.divider) {
                return (
                  <div
                    key={`divider-${idx}`}
                    className={`border-t border-line my-1 ${item.className || ''}`}
                    aria-hidden="true"
                  />
                );
              }
              const { icon: Icon, label, onClick, disabled, lock, kbd, className } = item;
              return (
                <button
                  key={label}
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  onClick={() => { setOpen(false); onClick(); }}
                  // className-Prop am Item erlaubt responsive Hide
                  // einzelner Einträge (z.B. Theme-Eintrag erscheint
                  // erst unter 880 px, wenn der dedizierte
                  // ThemeSwitch oben aus dem Header gewichen ist).
                  className={`w-full flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed text-left ${className || ''}`}
                  style={{ border: 'none', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  {Icon && <Icon size={14} aria-hidden="true" />}
                  <span className="flex-1">{label}</span>
                  {kbd && (
                    <kbd className="text-[10px] font-semibold bg-surface-2 border border-line px-1 py-0.5" style={{ borderRadius: 4 }}>
                      {kbd}
                    </kbd>
                  )}
                  {lock && <Lock size={10} className="text-ink-4" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── User menu (dropdown) ──────────────────────────────────────────

function UserMenu() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const session = useAuthStore((s) => s.session);
  const signOut = useAuthStore((s) => s.signOut);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!session) {
    return (
      <Link
        to="/login"
        className="inline-flex items-center gap-1 px-3 text-xs font-semibold bg-brand text-white"
        style={{ height: 32, borderRadius: 'var(--radius-md)' }}
      >
        Anmelden
      </Link>
    );
  }

  const handleLogout = async () => {
    setOpen(false);
    await signOut();
    navigate('/login');
  };

  const planLabel = profile?.plan === 'pro' ? 'PRO'
    : profile?.plan === 'team' ? 'TEAM'
    : 'FREE';
  const planClass = profile?.plan === 'free'
    ? 'bg-surface-2 text-ink-3 border border-line'
    : 'text-[#3c2d00]';
  const planStyle = profile?.plan === 'free'
    ? {}
    : { background: 'var(--color-pro)' };
  const initial = (profile?.display_name?.[0] || profile?.email?.[0] || 'U').toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 bg-surface-2 border border-line transition-colors hover:bg-surface"
        style={{ height: 34, padding: '2px 4px 2px 2px', borderRadius: 999 }}
      >
        <span
          className="inline-flex items-center justify-center text-white text-xs font-bold"
          style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1d51ff 0%, #7c3aed 100%)',
          }}
          aria-hidden="true"
        >
          {initial}
        </span>
        <span
          className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 ${planClass}`}
          style={{ borderRadius: 999, ...planStyle }}
        >
          {planLabel}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 bg-surface border border-line-strong shadow-2xl overflow-hidden z-50"
          style={{ width: 240, borderRadius: 'var(--radius-md)' }}
        >
          <div className="px-4 py-3 border-b border-line">
            <p className="text-xs font-bold text-ink truncate">
              {profile?.display_name || profile?.email}
            </p>
            <p className="text-[10px] text-ink-3 truncate">{profile?.email}</p>
          </div>
          <div className="py-1">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <Settings size={14} aria-hidden="true" />
              Einstellungen
            </Link>
            <Link
              to="/projects"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <Folder size={14} aria-hidden="true" />
              Meine Projekte
            </Link>
            {profile?.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                role="menuitem"
                className="flex items-center gap-3 px-4 py-2 text-xs text-warn hover:bg-surface-2"
              >
                <Shield size={14} aria-hidden="true" />
                Admin Panel
              </Link>
            )}

            {/* Hilfe & Rechtliches — Pflicht-Footer-Links, die sonst
                nur auf den Auth-Pages (Login/Register/Reset) sichtbar
                wären. AVV / SLA / Leistungsbeschreibung / Subprozessoren
                bleiben raus, sind aus AGB bzw. Datenschutz cross-
                verlinkt. */}
            <div className="border-t border-line my-1" />
            <WissenMenuLink onClick={() => setOpen(false)} />
            <Link
              to="/faq"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <HelpCircle size={14} aria-hidden="true" />
              FAQ
            </Link>
            <Link
              to="/legal/imprint"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <Info size={14} aria-hidden="true" />
              Impressum
            </Link>
            <Link
              to="/legal/privacy"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <Lock size={14} aria-hidden="true" />
              Datenschutz
            </Link>
            <Link
              to="/legal/terms"
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
            >
              <FileText size={14} aria-hidden="true" />
              AGB
            </Link>

            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className="flex items-center gap-3 w-full px-4 py-2 text-xs text-err hover:bg-surface-2 border-t border-line"
            >
              <LogOut size={14} aria-hidden="true" />
              Abmelden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AppHeader (top-level export) ──────────────────────────────────

// Theme-Cycle für das Overflow-Menu-Item bei sehr engen Headern,
// wenn der dedizierte ThemeSwitch nicht mehr in die Zeile passt.
// Reihenfolge bewusst Hell → System → Dunkel — entspricht der
// visuellen „Helligkeit aufsteigend / absteigend"-Lesart der Icons
// und ist konsistent mit der Reihenfolge im ThemeSwitch-Picker oben.
const THEME_CYCLE = { light: 'system', system: 'dark', dark: 'light' };
const THEME_LABEL = { light: 'Hell', system: 'System', dark: 'Dunkel' };
const THEME_ICON = { light: Sun, system: Monitor, dark: Moon };

export default function AppHeader() {
  const navigate = useNavigate();
  const {
    calculationName, elements, m0, mode, thermal,
    newProject, loadProject, isDirty,
  } = useToleranceStore();
  const profile = useAuthStore((s) => s.profile);
  const isPro = ['pro', 'team'].includes(profile?.plan);
  const [theme, setTheme] = useTheme();

  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  // .tcp-Save geht jetzt durch einen Bestätigungs-Dialog (Task #40):
  // Filename editierbar, Speicherort-Picker wenn der Browser ihn
  // unterstützt. `saveTcpContent` cached den serialisierten JSON-String
  // damit der Dialog ihn unverändert weiterreichen kann — wir
  // re-serialisieren nicht zwischen "Klick" und "Bestätigung", sonst
  // könnte der User noch was editieren und sich wundern.
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveTcpContent, setSaveTcpContent] = useState('');

  // Auto-fetch projects when Pro lands on the calculator route. This
  // populates the project picker in the Breadcrumb's ProjectChip and
  // the ⌘K palette. Free users never call /api/projects.
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const selectProjectAction = useProjectStore((s) => s.selectProject);
  useEffect(() => {
    if (isPro) fetchProjects();
  }, [isPro, fetchProjects]);

  // Allow any component (e.g. the MeasureTable empty-state CTA) to
  // open the template gallery without having to thread state down.
  // Same pattern as the ⌘K open dispatcher above (App.jsx:235).
  useEffect(() => {
    const onCustom = () => setTemplatesOpen(true);
    window.addEventListener('toleranz:open-templates', onCustom);
    return () => window.removeEventListener('toleranz:open-templates', onCustom);
  }, []);

  // Reset gating — softened per user feedback (the old version popped
  // a modal even for the trivial "Pro with saved work" case). Now we
  // only interrupt when there's actual data-loss risk: Pro user with
  // unsaved edits. Free users no longer have a calc-credit penalty
  // for restarting (the counter ticks on BERECHNEN, not on reset), so
  // their reset is direct as well. A toast acknowledges the action.
  const resetNow = () => {
    newProject();
    useProjectStore.getState().detachCurrentCalculation();
    toast.success('Neue Berechnung gestartet');
  };
  const handleReset = () => {
    if (isPro && isDirty) setShowConfirmNew(true); else resetNow();
  };

  const confirmNewProject = () => {
    resetNow();
    setShowConfirmNew(false);
  };

  const handleSave = () => {
    if (!isPro) { navigate('/settings/billing'); return; }
    // thermal landet im Envelope (Task #27 Phase I) damit aktivierte
    // Wärme-Berechnungen über den .tcp-Roundtrip nicht verloren gehen.
    // serializeProject schreibt thermal nur, wenn enabled=true ist —
    // sonst bleibt der Envelope strukturell wie zuvor.
    //
    // Statt sofort runterzuladen (Vor-Task-#40) öffnen wir den
    // SaveTcpDialog: User kann den Filename bestätigen/ändern und ggf.
    // den Speicherort wählen (File System Access API in Chrome/Edge).
    const json = toTcpString({ projectName: calculationName, elements, m0, mode, thermal });
    setSaveTcpContent(json);
    setSaveOpen(true);
  };

  const handleLoad = () => {
    if (!isPro) { navigate('/settings/billing'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.tcp,.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = parseTcp(ev.target.result);
          loadProject(parsed, parsed.name, null, null, { proMode: true });
          useProjectStore.getState().detachCurrentCalculation();
        } catch (err) {
          if (err instanceof TcpParseError) {
            const detail = err.details?.[0]
              ? ` (${err.details[0].path.join('.')}: ${err.details[0].message})`
              : '';
            toast.error(`Datei konnte nicht geladen werden: ${err.message}${detail}`);
          } else {
            toast.error('Fehler beim Laden: ' + err.message);
          }
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  return (
    <header
      className="bg-surface border-b border-line flex items-center gap-3.5 px-4 shrink-0"
      style={{ height: 'var(--header-h)' }}
    >
      <Link to="/" className="inline-flex items-center hover:opacity-80 transition-opacity">
        {/* Volle Brand (Σ + TOLERANZ.APP) ab 880 px, darunter nur die
            Bildmarke — hält den Header bei Dritt-Snap und kleinen
            Tablets aufgeräumt. Wrapper-<span> trägt die Sichtbarkeits-
            klasse, weil <Brand> selbst intern `inline-flex` als display
            setzt; ein className am Brand würde sonst gegen die feste
            Display-Klasse kollidieren und nicht greifen. */}
        <span className="hidden min-[880px]:inline-flex">
          <Brand
            size="sm"
            wordmarkClassName="text-ink"
            accentClassName="text-brand"
          />
        </span>
        <span className="inline-flex min-[880px]:hidden">
          <Brand
            size="sm"
            iconOnly
            wordmarkClassName="text-ink"
            accentClassName="text-brand"
          />
        </span>
      </Link>

      <Breadcrumb onOpenNewProject={() => setNewProjectOpen(true)} />

      <SearchInput />

      <div className="flex-1" />

      <div className="flex items-center gap-1.5">
        {/* "Neu" bleibt immer sichtbar — häufigste Action, gehört nicht
            ins Overflow-Menu. */}
        <HeaderButton icon={Plus} label="Neu" onClick={handleReset} title="Neue Berechnung starten" />

        {/* Volle Action-Reihe ab 1076 px sichtbar. Darunter (Drittel-
            Snap auf 4K, kleine Tablets) wandert sie ins Overflow-Menu. */}
        <div className="hidden min-[1076px]:flex items-center gap-1.5">
          <HeaderButton
            icon={Sparkles}
            label="Vorlagen"
            tour="templates-button"
            onClick={() => setTemplatesOpen(true)}
            title="Vorlagen-Galerie öffnen"
          />
          <HeaderButton
            icon={FolderOpen}
            label="Laden"
            onClick={handleLoad}
            lock={!isPro}
            title={isPro ? '.tcp-Datei laden' : 'Laden - Pro-Feature'}
          />
          <HeaderButton
            icon={Save}
            label="Speichern"
            onClick={handleSave}
            disabled={elements.length === 0}
            kbd="⌘S"
            lock={!isPro}
            title={isPro ? 'Berechnung als .tcp speichern' : 'Speichern - Pro-Feature'}
          />
          <HeaderButton
            icon={FileDown}
            label="Export"
            tour="export-button"
            onClick={() => setExportOpen(true)}
            disabled={elements.length === 0}
            primary
            title="PDF / CSV exportieren"
          />
        </div>

        {/* Overflow-Menu bei ≤ 1075 px — kapselt die vier Action-Items.
            Pro-Gate (Lock-Icon im Item) bleibt erhalten, kbd-Hint
            ebenfalls — wirkt im Dropdown sogar lesbarer als im Header. */}
        <div className="inline-flex min-[1076px]:hidden">
          <ActionsOverflowMenu
            items={[
              {
                icon: Sparkles, label: 'Vorlagen',
                onClick: () => setTemplatesOpen(true),
              },
              {
                icon: FolderOpen, label: 'Laden',
                onClick: handleLoad,
                lock: !isPro,
              },
              {
                icon: Save, label: 'Speichern',
                onClick: handleSave,
                disabled: elements.length === 0,
                kbd: '⌘S',
                lock: !isPro,
              },
              {
                icon: FileDown, label: 'Export',
                onClick: () => setExportOpen(true),
                disabled: elements.length === 0,
              },
              // Theme-Eintrag taucht erst unter 880 px auf — bis dahin
              // existiert der dedizierte ThemeSwitch rechts neben dem
              // UserMenu. Klick zykelt Hell → System → Dunkel → Hell,
              // ohne dass das Menü drei separate Einträge spendieren
              // müsste; das ist im Compact-Mode der bessere Trade-off.
              { divider: true, className: 'min-[880px]:hidden' },
              {
                icon: THEME_ICON[theme],
                label: `Theme: ${THEME_LABEL[theme]}`,
                onClick: () => setTheme(THEME_CYCLE[theme]),
                className: 'min-[880px]:hidden',
              },
            ]}
          />
        </div>
      </div>

      {/* Dedizierter ThemeSwitch ab 880 px. Darunter wandert Theme
          als Cycle-Item ins ActionsOverflowMenu (siehe items oben). */}
      <div className="hidden min-[880px]:inline-flex">
        <ThemeSwitch />
      </div>
      <UserMenu />

      <ConfirmDialog
        open={showConfirmNew}
        title="Ungespeicherte Änderungen verwerfen?"
        message="Beim Starten einer neuen Berechnung gehen die aktuellen Änderungen verloren. Speichere sie zuerst, falls du sie behalten willst."
        confirmLabel="Verwerfen & neu starten"
        onConfirm={confirmNewProject}
        onCancel={() => setShowConfirmNew(false)}
        variant="amber"
      />
      {exportOpen && (
        <Suspense fallback={null}>
          <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
        </Suspense>
      )}
      {templatesOpen && (
        <Suspense fallback={null}>
          <TemplateGalleryModal open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
        </Suspense>
      )}
      {newProjectOpen && (
        <Suspense fallback={null}>
          <NewProjectModal
            onClose={() => setNewProjectOpen(false)}
            onSuccess={(project) => { selectProjectAction(project); setNewProjectOpen(false); }}
          />
        </Suspense>
      )}
      <SaveTcpDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        defaultName={calculationName}
        jsonContent={saveTcpContent}
      />
    </header>
  );
}
